import type { JSX, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { button, monoLabel } from './look';
import { useEscape } from './use-escape';

/**
 * The centred dialog of the design's "Add source" and "Allow user": an
 * accent eyebrow, a title, a close button, and whatever the body is. 900px
 * wide for the source catalog, 520 for a short form. On a phone it is a
 * bottom sheet that rises to 56px below the top, full width.
 */
export function Modal({
  eyebrow,
  title,
  width = 520,
  onClose,
  children,
  className,
  tall = false,
}: {
  eyebrow: string;
  title: string;
  width?: number;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  /** On a phone, take the whole sheet height (the design's add-source catalog). */
  tall?: boolean;
}): JSX.Element {
  useEscape(onClose);
  // Portalled to <body>: pages enter with a transform animation (`rise`, fill
  // mode both), and a transformed ancestor would pin `fixed` to the page box
  // instead of the viewport, clipping the overlay.
  return createPortal(
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 z-40 bg-[oklch(0_0_0/0.5)] backdrop-blur-[3px]"
      />
      <div className="pointer-events-none fixed inset-0 z-[41] grid items-end justify-items-center pt-14 tab:place-items-center tab:p-6">
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          className={cn(
            'bg-surf border-line shadow-panel animate-sheet-up pointer-events-auto flex max-h-full w-full flex-col overflow-hidden rounded-t-[20px] border tab:max-h-[90vh] tab:animate-[rise_.3s_ease_both] tab:rounded-[20px]',
            tall && 'h-full tab:h-auto',
            className,
          )}
          style={{ maxWidth: width }}
        >
          <div className="border-line flex items-center gap-4 border-b px-7 py-[22px]">
            <div className="flex-1">
              <div className={cn(monoLabel, 'text-acc text-[11px]')}>{eyebrow}</div>
              <div className="mt-0.5 text-lg font-semibold">{title}</div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className={button.close}>
              ×
            </button>
          </div>
          {children}
        </div>
      </div>
    </>,
    document.body,
  );
}
